import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useLocation } from "react-router";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../../firebase";

export default function PasswordReset() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [email, setEmail] = useState("");
  const [oobCode, setOobCode] = useState("");

  useEffect(() => {
    // URLからoobCodeを取得
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get("oobCode");

    if (code) {
      setOobCode(code);
      // コードの有効性を確認し、関連するメールアドレスを取得
      verifyPasswordResetCode(auth, code)
        .then((email) => {
          setEmail(email);
        })
        .catch((error) => {
          console.error("無効なリセットコード:", error);
          setResetError(
            "このパスワードリセットリンクは無効または期限切れです。",
          );
        });
    } else {
      setResetError(
        "リセットコードが見つかりません。有効なリンクからアクセスしてください。",
      );
    }
  }, [location]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validatePasswords = () => {
    if (password.length < 6) {
      setPasswordError("パスワードは6文字以上である必要があります");
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError("パスワードが一致しません");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    try {
      setLoading(true);

      // Firebaseでパスワードをリセット
      await confirmPasswordReset(auth, oobCode, password);

      // 成功ダイアログを表示
      setOpenDialog(true);
    } catch (error: any) {
      console.error("パスワードリセットエラー:", error);
      if (error.code === "auth/weak-password") {
        setPasswordError(
          "安全性の低いパスワードです。より強力なパスワードを使用してください",
        );
      } else if (error.code === "auth/expired-action-code") {
        setResetError(
          "このリセットリンクは期限切れです。再度パスワードリセットを行ってください",
        );
      } else {
        setResetError("エラーが発生しました。後でもう一度お試しください");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    navigate("/login");
  };

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* ローディング中のバックドロップ */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">
          処理中です。しばらくお待ちください...
        </Typography>
      </Backdrop>

      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        パスワードリセット
      </Typography>

      {resetError ? (
        <>
          <Typography color="error" sx={{ mt: 2 }}>
            {resetError}
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => navigate("/")}
          >
            トップページへ
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {email
              ? `${email} の新しいパスワードを入力してください`
              : "新しいパスワードを入力してください"}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1, width: "100%" }}
          >
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="新しいパスワード"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!passwordError}
                  helperText={passwordError}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="新しいパスワード（確認）"
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={!oobCode || !!resetError}
            >
              パスワードを変更する
            </Button>
          </Box>
        </>
      )}

      {/* Dialog for password reset confirmation */}
      <Dialog open={openDialog} aria-labelledby="password-reset-dialog-title">
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            パスワードの変更が完了しました
          </Typography>
          <DialogContentText>
            新しいパスワードでログインしてください。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" autoFocus>
            ログイン画面へ
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
